import { useContext, useEffect, useMemo, useState } from 'react';
import { FilterContext, IndicatorsType, TopicLevelType } from '../context';
import {
  arrayToSimpleObj,
  getPercentageValue,
  getRationPercentValue,
  handleMentionData,
  sortTimeListFunc,
  supplementSingleData,
} from '@op/utils';
import { formatDateToServerDataFunc, getNeedTime } from '@op/utils/time';
import { getTopicTimeline } from '@op/services';
import { useTopicTrackingLoading } from '@op/models/loading/useTopicTrackingLoading';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';

type TableDataType = {
  header: Array<any>;
  data: Array<any>;
};

function dealwithBlockData(data: { mentions: any; sum: any; total: number }) {
  const { mentions, total } = data;

  return {
    data: (mentions || []).map((item: any) => ({
      name: item.key,
      count: item.value,
      child: item.child_topic_stats,
    })),
    total: total || 0,
  };
}

function dealwithTimeLineData(
  data: {
    timeline_of_start_time: any;
    timeline_of_end_time: any;
    timeline: any;
    sorts: any;
    sum: any;
  },
  filter: any,
  indicators: IndicatorsType,
  isNikke = false,
) {
  const { timeline_of_start_time, timeline_of_end_time, timeline, sorts, sum } =
    data;

  const dateType = filter.date_type;
  let sortList = sorts || [];
  if (isNikke) {
    sortList = [
      ...sortList,
      ...(filter?.topic || []).filter(
        (key: string) => !(sortList || []).includes(key),
      ),
    ];
  }

  const list = supplementSingleData(
    timeline_of_start_time,
    timeline_of_end_time,
    timeline?.map((item: { count: any; value: any; key: any }) => ({
      ...item,
      count: item.count || item.value,
      name: item.key,
    })),
    dateType,
    sortList,
  );

  const needTime = getNeedTime(dateType);
  let { newArr, timeArr } = handleMentionData(list, needTime);
  newArr?.sort(
    (a: { name: any }, b: { name: any }) =>
      sortList?.indexOf(a.name) - sortList?.indexOf(b.name),
  );
  const sumMap = arrayToSimpleObj(sum, 'value', (item: { time: any }) =>
    formatDateToServerDataFunc(dateType, item.time),
  );

  if (
    [
      'mentions_ratio',
      'negative_mentions',
      'positive_mentions',
      'engagement_ratio',
    ].includes(indicators)
  ) {
    newArr = newArr.map((item: { data: any[] }) => {
      return {
        ...item,
        originData: item.data,
        data: item.data.map((value: any, i: string | number) => {
          return getPercentageValue(value, sumMap[timeArr[i]], 10);
        }),
      };
    });
  }

  const result: any = {
    timeline: { seriesDataArray: newArr || [], sumMap, xAxisData: timeArr },
    tableInfo: { header: [], data: [] },
  };

  const tableData: Array<any> = timeArr.map(
    (time: string | number, index: string | number) => {
      const itemObj: any = { time };
      newArr.forEach(({ data, name }) => {
        itemObj[name] = data[index];
      });
      itemObj['total'] = sumMap[time];
      return itemObj;
    },
  );
  tableData.sort(sortTimeListFunc('time', 'desc'));
  result['tableInfo'] = { header: sortList, data: tableData };

  return result;
}

export function useTimelineDataLocalParams({
  filter,
  topicLevel,
  dealwithParams,
  isNikke,
}: {
  filter: any;
  topicLevel: TopicLevelType;
  dealwithParams?: (value: any, indicators: IndicatorsType) => any;
  isNikke?: boolean;
}) {
  const [indicators, setIndicators] = useState<IndicatorsType>('mentions');
  const [barAndLine, setBarAndLine] = useState({
    seriesDataArray: [],
    sumMap: {},
    xAxisData: [],
  });
  const [blockData, setBlockData] = useState({ data: [], total: 0 });
  const [apiError, setApiError] = useState(false);
  const [tableInfo, setTableInfo] = useState<TableDataType>({
    header: [],
    data: [],
  });

  const metricMap: any = {
    engagement_ratio: 'engagement',
    mentions_ratio: 'mentions',
  };
  const params = useMemo(() => {
    const metric = metricMap?.[indicators] || indicators;
    function getOtherParams() {
      const params: any = {
        top_n: 15,
        need_others: topicLevel === 'L2',
      };
      return params;
    }

    return {
      ...filter,
      ...getOtherParams(),
      metrics: [metric],
      sort_item: metric,
      sort_model: 'desc',
      only_query_user_param: false,
      need_child_topic_stats: true,
      topic_level: topicLevel,
    };
  }, [indicators, filter]);

  const { loading, wrapperRequest } = useCancelRequestHook();

  const requestTopicTimeline = (filter: any) => {
    setApiError(false);
    setBarAndLine({ seriesDataArray: [], sumMap: {}, xAxisData: [] });
    setTableInfo({ header: [], data: [] });
    setBlockData({ data: [], total: 0 });
    const params = dealwithParams ? dealwithParams(filter, indicators) : filter;
    wrapperRequest(cancelFunc =>
      getTopicTimeline(params, cancelFunc).then((data: any) => {
        const { timeline, tableInfo } = dealwithTimeLineData(
          data,
          filter,
          indicators,
          isNikke,
        );
        const blockData = dealwithBlockData(data);
        setBarAndLine(timeline);
        setTableInfo(tableInfo);
        setBlockData(blockData);
      }),
    );
  };

  useEffect(() => {
    if (!params.edition_unified_id) return;
    requestTopicTimeline(params);
  }, [params]);

  return {
    blockData,
    barAndLine,
    loading,
    apiError,
    tableInfo,
    indicators,
    topicLevel,
    setIndicators,
  };
}

/**
 * 获取图表的方法
 * @returns
 */
export default function useTimelineData({}: {}) {
  const { filter, topicLevel } = useContext(FilterContext);

  const data = useTimelineDataLocalParams({ filter, topicLevel });

  return data;
}

function getRatioDefaultValue() {
  return {
    seriesDataArray: [],
    sumMap: {},
    xAxisData: [],
  };
}
export function useTopicLevelTwoTimeline({
  index,
  topic,
  topicLevel,
  filter,
  indicatorsType = 'mentions',
}: {
  index?: number;
  topic?: Array<string>;
  topicLevel?: TopicLevelType;
  filter: any;
  indicatorsType?: IndicatorsType;
}) {
  const { canRequest, finishLoading, needLoading } = useTopicTrackingLoading({
    index,
    key: 'topicTrackingTimeline',
  });
  const [commonLoading, setCommonLoading] = useState(false);

  const [timelineMap, setTimelineMap] = useState<any>({
    [indicatorsType]: getRatioDefaultValue(),
    [`${indicatorsType}_ratio`]: getRatioDefaultValue(),
  });

  const [apiError, setApiError] = useState(false);

  function getTimelineMap(barAndLine: any) {
    const localChartDataMap: any = {};
    const { seriesDataArray, xAxisData } = barAndLine;
    seriesDataArray?.forEach(({ data, name }: any) => {
      const maxIndex = data.length - 1;
      const minIndex = 0;
      const oldValue = data[minIndex];
      const newValue = data[maxIndex];
      const key = name.toLocaleLowerCase();
      localChartDataMap[key] = {
        x: xAxisData,
        y: data,
        oldValue,
        newValue,
        newTime: [xAxisData[maxIndex]],
        oldTime: [xAxisData[minIndex]],
        ration: getRationPercentValue(newValue, oldValue, 4),
      };
    });
    return localChartDataMap;
  }

  const getParams = () => {
    let topicMap = {};
    if (topic) topicMap = { topic };

    return {
      top_n: 1000,
      ...filter,
      metrics: [indicatorsType],
      sort_item: indicatorsType,
      sort_model: 'desc',
      ...topicMap,
      only_query_user_param: false,
      topic_level: topicLevel,
    };
  };

  const requestTopicTimeline = () => {
    setApiError(false);
    setCommonLoading(true);
    const params = getParams();
    getTopicTimeline(params)
      .then((data: any) => {
        const { timeline } = dealwithTimeLineData(
          data,
          filter,
          indicatorsType === 'mentions' ? 'mentions_ratio' : indicatorsType,
        );

        const mentionsTimeline = JSON.parse(
          JSON.stringify({
            ...(timeline || {}),
            seriesDataArray: (timeline.seriesDataArray || []).map(item => {
              return { ...item, data: item.originData };
            }),
          }),
        );

        setTimelineMap({
          [indicatorsType]: getTimelineMap(mentionsTimeline),
          [`${indicatorsType}_ratio`]: getTimelineMap(timeline),
        });
      })
      .catch(() => {
        setApiError(true);
      })
      .finally(() => {
        finishLoading('topicTrackingTimeline', typeof index === 'number');
        setCommonLoading(false);
      });
  };

  useEffect(() => {
    if (filter.edition_unified_id && canRequest && index !== undefined) {
      requestTopicTimeline();
    }
  }, [canRequest]);

  useEffect(() => {
    if (!filter.date_type) return;
    if (index === undefined) {
      requestTopicTimeline();
    }
  }, [filter]);

  return {
    timelineMap,
    loading: needLoading,
    apiError,
    topicLevel,
    commonLoading,
  };
}
